import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import type { SfAnswerRow, SfCandidate, SfChose, SfField, SfFieldKey } from '@claims/core';
import { actionToneOf, sfBlockerText, SF_FIELD_SHORT, SF_WHY_TEXT, dolLabel, matchesWords, pacificStamp, searchWordsOf } from '@claims/core';
import { countOf } from './count.js';
import { api, type ActResult } from './api.js';
import { toneProps } from './action-tone.js';
import { SidePill } from './components.js';
import { Ic } from './icons.js';
import { usePress } from './press.js';
import { PressButton } from './press-button.js';
import { FRAME_SEARCH, Shell, Topbar } from './Shell.js';
import { EmptyState, QueryState, STATE_WORDS } from './states.js';
import { useRowsChangedGuard } from './list/hold.js';

// "State Farm: needs info" (Rod 2026-10-08 ~4:20 pm "if we have the info we go", router spec docs/specs/2026-10-08-statefarm-confirm-then-
// file.md): the robot fills everything our records state; a row here is a fact NO record states (or records state differently). A person
// supplies it once and the robot files with no further review. Its form opens in place, in the page's own flow (no scroll box inside it).
type Answer = { value: unknown; chose: SfChose };
const show = (v: unknown): string => {
  if (v == null) return '';
  if (Array.isArray(v)) return v.map((x) => (x === 'MEDPAY' ? 'Med Pay' : String(x))).join(', ');
  if (typeof v === 'object') { const o = v as Record<string, unknown>; return [o.year, o.make, o.model].filter(Boolean).join(' '); }
  return String(v);
};
const sourceText = (c: { label: string; source: { kind: string; object: string | null } }) => c.label;
const sameV = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b) || (typeof a === 'string' && typeof b === 'string' && a.trim().toUpperCase() === b.trim().toUpperCase());

/** The supply press reaches the robot only once the router's Controller reads supplied facts (off on 2026-10-09). */
const SUPPLY_LIVE = false;

function Candidate({ f, c, a, set, name }: { f: SfField; c: SfCandidate; a: Answer | undefined; set: (x: Answer) => void; name: string }) {
  const evidenceOnly = c.value == null;
  const picked = !!a && a.chose === 'candidate' && sameV(a.value, c.value);
  return (
    <li className={`sf-c${c.selectable ? '' : ' off'}${picked ? ' on' : ''}`}>
      {evidenceOnly ? <span className="sf-ev-dot" aria-hidden="true" /> : (
        <input type="radio" name={name} disabled={!c.selectable} checked={picked} onChange={() => set({ value: c.value, chose: 'candidate' })} aria-label={`${show(c.value)} from ${sourceText(c)}`} />
      )}
      <span className="sf-cv">
        {evidenceOnly ? null : <b>{show(c.value)}</b>}
        <span className="sf-src">{sourceText(c)}{c.atFault === true ? <span className="sf-af">at fault</span> : c.atFault === false ? <span className="sf-naf">not at fault</span> : null}</span>
        {c.source.snippet && evidenceOnly ? <q className="sf-snip">{c.source.snippet}</q> : null}
        {(c.evidence ?? []).map((e, i) => <q key={i} className="sf-snip"><span className="mt">{e.label}: </span>{e.snippet}</q>)}
        {!c.selectable && c.why && c.why !== 'evidence_only' ? <span className="sf-why">Can't use: {SF_WHY_TEXT[c.why] ?? c.why}</span> : null}
      </span>
    </li>
  );
}

function TypedInput({ f, a, set }: { f: SfField; a: Answer | undefined; set: (x: Answer) => void }) {
  const typed = a && a.chose === 'typed' ? a.value : undefined;
  if (f.input === 'select') {
    return (
      <select className="sf-in" value={typed == null ? '' : String(typed)} onChange={(e) => {
        const o = (f.options ?? []).find((x) => String(x.value) === e.target.value);
        if (o) set({ value: o.value, chose: 'typed' });
      }}>
        <option value="">Choose…</option>
        {(f.options ?? []).map((o) => <option key={String(o.value)} value={String(o.value)}>{o.label}</option>)}
      </select>
    );
  }
  if (f.input === 'multi') {
    const cur = Array.isArray(typed) ? typed as unknown[] : [];
    return (
      <span className="sf-multi">{(f.options ?? []).map((o) => (
        <label key={String(o.value)} className="sf-chk"><input type="checkbox" checked={cur.includes(o.value)}
          onChange={(e) => set({ value: e.target.checked ? [...cur, o.value] : cur.filter((x) => x !== o.value), chose: 'typed' })} />{o.label}</label>
      ))}</span>
    );
  }
  if (f.input === 'attest') {
    const cur = typed && typeof typed === 'object' ? typed as { basis?: string } : {};
    return (
      <span className="sf-multi">{(f.options ?? []).map((o) => (
        <label key={String(o.value)} className="sf-chk"><input type="radio" name={`attest-${f.key}`} checked={cur.basis === o.value}
          onChange={() => set({ value: { attested: true, basis: o.value }, chose: 'typed' })} />{o.label}</label>
      ))}</span>
    );
  }
  if (f.input === 'vehicle') {
    const cur = (typed && typeof typed === 'object' ? typed : {}) as { year?: string; make?: string; model?: string };
    const put = (k: 'year' | 'make' | 'model', v: string) => set({ value: { ...cur, [k]: v }, chose: 'typed' });
    return (
      <span className="sf-veh">
        <input className="sf-in sm" placeholder="Year" inputMode="numeric" value={cur.year ?? ''} onChange={(e) => put('year', e.target.value)} />
        <input className="sf-in sm" placeholder="Make" value={cur.make ?? ''} onChange={(e) => put('make', e.target.value)} />
        <input className="sf-in" placeholder="Model" value={cur.model ?? ''} onChange={(e) => put('model', e.target.value)} />
      </span>
    );
  }
  return <input className="sf-in" value={typed == null ? '' : String(typed)} placeholder={f.key === 'location' ? 'City, ST  or  Street, City, ST' : f.key === 'policy_number' ? 'State Farm policy number' : 'Type it'}
    onChange={(e) => set({ value: e.target.value, chose: 'typed' })} />;
}

function FieldForm({ f, a, set, rowId }: { f: SfField; a: Answer | undefined; set: (x: Answer) => void; rowId: string }) {
  const name = `${rowId}-${f.key}`;
  const picks = f.candidates.filter((c) => c.value != null);
  const evidence = f.candidates.filter((c) => c.value == null);
  return (
    <div className="sf-f" role="group" aria-labelledby={`${name}-q`}>
      <div className="sf-q" id={`${name}-q`}><b>{f.question}</b>{f.conflictOnly ? <span className="sf-tag">our records disagree</span> : null}</div>
      {picks.length ? <ul className="sf-cl">{picks.map((c, i) => <Candidate key={i} f={f} c={c} a={a} set={set} name={name} />)}</ul> : null}
      {evidence.length ? <ul className="sf-cl ev">{evidence.map((c, i) => <Candidate key={i} f={f} c={c} a={a} set={set} name={name} />)}</ul> : null}
      {f.staff_default ? (
        <label className={`sf-staff${a && a.chose === 'staff_default' ? ' on' : ''}`}>
          <input type="radio" name={name} checked={!!a && a.chose === 'staff_default'} onChange={() => set({ value: f.staff_default!.value, chose: 'staff_default' })} />
          <span><b>{show(f.staff_default.value)}</b> <span className="sf-src">{f.staff_default.label}</span>
            {f.staff_default.acceptedWhen === 'no_police_report' ? <span className="sf-why">Accepted only when there is no police report</span> : null}</span>
        </label>
      ) : null}
      {f.staff_default_pending ? <p className="sf-note mt">What staff usually enter here: asked, no answer yet.</p> : null}
      <div className="sf-type"><span className="mt">{picks.length ? 'Or enter it:' : 'Enter it:'}</span><TypedInput f={f} a={a} set={set} /></div>
    </div>
  );
}

function RequestRow({ r, open, toggle }: { r: SfAnswerRow; open: boolean; toggle: () => void }) {
  const [answers, setAnswers] = useState<Partial<Record<SfFieldKey, Answer>>>(() => Object.fromEntries(r.request.fields.filter((f) => f.prefill).map((f) => [f.key, { value: f.prefill!.value, chose: (f.prefill!.source.kind === 'staff_practice' ? 'staff_default' : 'candidate') as SfChose }])));
  const [res, setRes] = useState<ActResult | null>(null);
  const isOpen = r.request.state === 'needs_answers';
  const answered = r.request.fields.filter((f) => answers[f.key] != null && answers[f.key]!.value !== '' && answers[f.key]!.value != null).length;
  // ONE PRESS (press.ts): "Saving and filing" with its spinner from the click; a second press while one is out sends nothing
  const press = usePress(async () => {
    try { setRes(await api.sfSupply(r.id, { answers })); } catch { setRes({ result: 'failed', code: 'the console did not answer; try again' }); }
  });
  return (
    <div className={`mb sf-row${isOpen ? ' sf-open-req' : ' sf-blocked-req'}`} data-row="">
      <div className="mbh sf-h">
        <span className="sf-urg">{isOpen ? <span className="utag" title="One or more facts are missing"><span className="sr">Urgent: </span>Needs info</span> : <span className="sf-blk">Blocked</span>}</span>
        <span className="sf-who">
          <a className="sf-client" href={`https://sweetjames.lightning.force.com/${r.matterId}`} target="_blank" rel="noreferrer" title={`${r.matter?.client ?? r.matterId} · Open in Salesforce`}>{r.matter?.client ?? r.matterId}</a>
          <span className="mt">{r.matter ? `${r.matter.name} · ${dolLabel(r.matter.dol)}` : r.matterId}</span>
        </span>
        <SidePill side={r.party === '1p' ? 'PLT' : 'DEF'} />
        <span className="sf-needs">{r.request.fields.map((f) => <span key={f.key} className={`sf-need${answers[f.key] ? ' got' : ''}`}>{SF_FIELD_SHORT[f.key]}</span>)}
          {r.request.blockers.map((b, i) => <span key={`b${i}`} className="sf-bl">{sfBlockerText(b.reason)}</span>)}</span>
        <span className="sf-wait mt">{pacificStamp(r.createdAt)}</span>
        {/* it opens the request's form in place, whose one press is a dry run not shown yet (SUPPLY_LIVE): grey, it only looks */}
        <button type="button" className="btn" onClick={toggle} aria-expanded={open} {...toneProps(actionToneOf('console', 'sf_form'))}>{open ? 'Close' : isOpen ? `Add ${r.request.fields.length} fact${r.request.fields.length === 1 ? '' : 's'}` : 'See why'}</button>
      </div>
      {open ? (
        <div className="sf-body">
          {r.request.notes.includes('first_party_learning_only') ? <p className="sf-note"><Ic name="lock" cls="sm" /> First party: the robot files this once State Farm's first-party pages are mapped (one supervised walk).</p> : null}
          {r.request.blockers.length ? <p className="sf-note"><b>Stopped by a fact check:</b> {r.request.blockers.map((b) => sfBlockerText(b.reason)).join(' · ')}.</p> : null}
          {r.request.fields.map((f) => <FieldForm key={f.key} f={f} a={answers[f.key]} rowId={r.id} set={(x) => { setAnswers((p) => ({ ...p, [f.key]: x })); setRes(null); }} />)}
          {isOpen ? (
            <div className="sf-foot">
              <span className="mt">{answered} of {r.request.fields.length} answered</span>
              <span className="grow" />
              {/* 2026-10-09: saving a supplied fact is not wired to the robot yet (button.sf_supply_info off): say what to do instead */}
              <span className="sf-note">Saving these here is not on yet. Open this claim on State Farm's website with the facts above.</span>
              {/* a dry run in this build (POST /api/claims/sf-answers/:id/supply): grey until the robot reads supplied facts and files with them */}
              {SUPPLY_LIVE ? <PressButton label="Save and file" className="btn lg pri" busy={press.busy} disabled={answered < r.request.fields.length} onPress={press.run}
                tone={actionToneOf('console', 'sf_supply')} /> : null}
            </div>
          ) : null}
          {res ? <p className={`sf-res ${res.result === 'dry_run' ? 'ok' : 'no'}`}>{res.result === 'dry_run' ? `Dry run: would ${res.describes}.` : `Not recorded: ${res.code}${res.code === 'switch_off' ? ' (this button is switched off)' : ''}.`}</p> : null}
        </div>
      ) : null}
    </div>
  );
}

export function SfAnswersPage() {
  const [q, setQ] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);
  const [showBlocked, setShowBlocked] = useState(false);
  const d = useQuery({ queryKey: ['sfAnswers'], queryFn: api.sfAnswers, staleTime: 60_000, refetchInterval: 120_000 });
  const rows = useMemo(() => (d.data?.rows ?? []).filter((r) => matchesWords([r.matter?.client, r.matter?.name, r.matter ? dolLabel(r.matter.dol) : null, r.matterId], searchWordsOf(q))), [d.data, q]);
  const open = rows.filter((r) => r.request.state === 'needs_answers'), blocked = rows.filter((r) => r.request.state === 'blocked');
  const listed = [...open, ...(showBlocked ? blocked : [])];
  // A CLICK THE ROWS MOVED UNDER IS DROPPED (list/hold.ts, the medrec console's post-ship review): a filed request leaves on the next read
  // (every 2 minutes), and the request under the pointer would change in the half second a click needs
  const guard = useRowsChangedGuard(listed.map((r) => `${r.id}|${r.request.state}|${r.request.fields.length}`).join('\n'));
  return (
    <Shell>
      <Topbar title="State Farm: needs info" sub="the robot could not file these" asOf={d.data?.asOf} q={q} setQ={setQ} ph={FRAME_SEARCH.stateFarm}
        stale={d.isRefetchError} onRetry={() => d.refetch()} />
      <div className="content tight">
        <QueryState q={d} what="the State Farm requests" loading="Loading the State Farm requests." rows />
        {d.data ? (
          <>
            <div className="listbar sf-band">
              <span><span className="utag">Needs info</span><b>{countOf(open.length)}</b> missing a fact, oldest first</span>
              <span className="mt"> · {countOf(blocked.length)} stopped by a fact check</span>
              {d.data.source === 'file' ? <span className="sf-tag">dry-run data</span> : null}
              <span className="grow" />
              <label className="sf-chk"><input type="checkbox" checked={showBlocked} onChange={(e) => setShowBlocked(e.target.checked)} />Show blocked</label>
            </div>
            {d.data.note ? <EmptyState words={d.data.note} /> : null}
            <div className="sf-list" onClickCapture={guard}>
              {listed.map((r) => <RequestRow key={r.id} r={r} open={openId === r.id} toggle={() => setOpenId(openId === r.id ? null : r.id)} />)}
            </div>
            {!open.length && !d.data.note ? <EmptyState words={q.trim() ? STATE_WORDS.noMatch(q) : 'No State Farm request is missing a fact right now.'} next={q.trim() ? { label: STATE_WORDS.clearSearch, onPress: () => setQ('') } : null} /> : null}
          </>
        ) : null}
      </div>
    </Shell>
  );
}
